import type { FC } from "react";
import { useMemo, useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { noop } from "lodash";

import { CombinedTypeGraph, LEGEND_POSITIONS } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";
import { OnOffSwitch } from "@similarweb/ui-components";

import { keywordGroupsTransforms } from "app/services/keyword-groups";
import { swSettings } from "app/common/services/swSettings";
import type { Filter } from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import type { IAddToDashboardConfig } from "app/components/React/WidgetContainer/types";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import type { IWidgetModelTypesType } from "app/components/widget/widget-types/Widget";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";

import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { webSourceToDeviceForApi } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import {
    GRAPH_API_URL,
    WIDGET_TITLE_KEY,
    WIDGET_TOOLTIP_KEY,
    getDurationSwitcherOptions,
} from "app/common/keyword-research/keywords-overview/trendOverTimeConsts";
import { getTrendOverTimeGraphConfig } from "./trendOverTimeGraphConfig";
import {
    GraphWrapper,
    LegendComponentWrapper,
    TrendOverTimeWrapper,
    WidgetOnOffWrapper,
} from "./TrendOverTimeStyledComponents";
import type { ITrendOverTimeResponseData } from "app/common/keyword-research/keywords-overview/trendOverTimeTypes";
import { GraphSourceTypeView } from "app/common/keyword-research/keywords-overview/trendOverTimeTypes";
import {
    getExcelUrl,
    getGraphItems,
    getUpdatedSubtitleFilters,
    legendFormatter,
    onDurationSwitcherItemClick,
    onGranularitySwitcherItemClick,
    useSwitcherItems,
} from "./trendOverTimeUtils";
import {
    getUpdatedGranularitySwitcherItems,
    hasPartialWeek,
} from "app/common/keyword-research/keywords-overview/trendOverTimeTimeframeHelpers";

interface ITrendOverTime {
    subtitleFilters: Filter[];
}

export const TrendOverTime: FC<ITrendOverTime> = ({ subtitleFilters = [] }) => {
    const { params } = useCurrentStateAndParams();
    const { duration, webSource, country, keyword, graphGranularity, graphDuration, graphSourceType } = params;
    const [rawGraphData, setRawGraphData] = useState<ITrendOverTimeResponseData>();
    const [isLoading, setIsLoading] = useState(true);

    const showSourceTypeBreakdown = graphSourceType === GraphSourceTypeView.BREAKDOWN;

    const { switcherItems: granularitySwitcherItems } = useSwitcherItems(
        undefined,
        graphDuration,
        getUpdatedGranularitySwitcherItems,
    );
    const { switcherItems: durationSwitcherItems } = useSwitcherItems(getDurationSwitcherOptions(duration));

    const durationObj = DurationService.getDurationData(duration);
    const isLast28 = duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    const { from, to, isWindow } = durationObj.forAPI;
    const key = keywordGroupsTransforms.getGroupIdFromUrlParam(keyword);

    const apiParams = {
        isWindow,
        from,
        to,
        device: webSourceToDeviceForApi[webSource],
        country,
        key,
        timeGranularity: graphGranularity,
        duration: graphDuration,
    };

    const excelUrl = getExcelUrl(apiParams);

    useEffect(() => {
        (async () => {
            setIsLoading(true);

            try {
                const data = await DefaultFetchService.getInstance().get<ITrendOverTimeResponseData>(
                    GRAPH_API_URL,
                    apiParams,
                );
                setRawGraphData(data);
            } catch (error) {
                Sentry.error(`Error fetching trend over time graph data for keyword analysis overview`, error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [graphDuration, graphGranularity]);

    const switcherDuration = {
        items: durationSwitcherItems,
        onItemClick: onDurationSwitcherItemClick(durationSwitcherItems, graphGranularity),
        initialSelectedItem: durationSwitcherItems.findIndex((item) => item.value === graphDuration),
    };

    const switcherGranularity = {
        items: granularitySwitcherItems,
        onItemClick: onGranularitySwitcherItemClick(granularitySwitcherItems),
        initialSelectedItem: granularitySwitcherItems.findIndex((item) => item.value === graphGranularity),
    };

    const onSourceTypeBreakdownToggle = () => {
        const isToggleOn = graphSourceType === GraphSourceTypeView.TOTAL;
        allTrackers.dispatchTrackEvent("Toggle", "switch", isToggleOn ? "Turn on breakdown" : "Turn off breakdown");
        proNavigator.updateQueryParams(
            {
                graphSourceType: isToggleOn ? GraphSourceTypeView.BREAKDOWN : GraphSourceTypeView.TOTAL,
            },
            { reload: false },
        );
    };

    const subtitleFiltersUpdatedForDuration =
        isLast28 && graphDuration === BasicDurations.LAST_TWENTY_EIGHT_DAYS
            ? subtitleFilters
            : getUpdatedSubtitleFilters(subtitleFilters, graphDuration, durationObj.raw.to);

    const addToDashboardConfig: IAddToDashboardConfig = {
        metric: "KeywordAnalysisTrendOverTime",
        type: "KeywordAnalysisTrendOverTimeGraph" as IWidgetModelTypesType,
        modelType: "fromKeyword",
        widgetWidth: 4,
        webSource,
        filters: {
            graphDuration,
            timeGranularity: graphGranularity,
            duration: params.duration,
            graphSourceType,
        },
    };

    const graphItems = useMemo(() => {
        return rawGraphData
            ? getGraphItems(
                  rawGraphData,
                  hasPartialWeek(graphGranularity, isLast28, durationObj),
                  showSourceTypeBreakdown,
              )
            : [];
    }, [rawGraphData, graphSourceType]);

    const noData =
        // @ts-expect-error FIXME: Refactor using narrow down checks
        graphItems?.length === 0 || graphItems?.every((item) => item?.data?.every((dataPoint) => !dataPoint.y));

    return (
        <TrendOverTimeWrapper>
            <WidgetContainer
                title={translate(WIDGET_TITLE_KEY)}
                tooltip={translate(WIDGET_TOOLTIP_KEY)}
                subtitleFilters={subtitleFiltersUpdatedForDuration ?? []}
                isLoading={isLoading}
                loaderWidth={"100%"}
                loaderHeight={400}
                noData={noData}
                withBorder={true}
                noDataTitle={translate("common.noresults")}
                noDataSubtitle={translate("keyword.analysis.overview.page.v2.trend_over_time.noData.subtitle")}
                dynamicSwitcherConfig={[switcherDuration, switcherGranularity]}
                excelUrl={excelUrl}
                isExcelDisabled={!swSettings.components.KeywordAnalysisV2Overview.resources.IsExcelAllowed}
                onExcelClick={noop}
                addToDashboardConfig={addToDashboardConfig}
            >
                {graphItems ? (
                    <GraphWrapper>
                        <CombinedTypeGraph
                            graphConfig={getTrendOverTimeGraphConfig(
                                duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS,
                                graphDuration,
                                graphGranularity as GranularityNames,
                                durationObj,
                                showSourceTypeBreakdown,
                            )}
                            data={graphItems}
                            // @ts-expect-error FIXME: Type 'Element' is not assignable to type 'string'
                            legendValueFilter={legendFormatter}
                            shouldPresentLegend={true}
                            shouldPresentValueAtLegend={true}
                            withoutTooltipsPadding={false}
                            legendPosition={LEGEND_POSITIONS.TOP}
                            keepSameLegendsOnDataUpdate={false}
                            componentNameForTracking={""}
                            legendsMaxWidth={250}
                            isSingleSelectionMode={false}
                            customLegendComponentWrapper={LegendComponentWrapper}
                            actionButtons={[
                                <WidgetOnOffWrapper key="OnOffSwitch">
                                    <OnOffSwitch
                                        isSelected={showSourceTypeBreakdown}
                                        isDisabled={false}
                                        onLabel={translate(
                                            "keyword.analysis.overview.page.v2.trend_over_time.source_type_breakdown.toggle",
                                        )}
                                        onClick={onSourceTypeBreakdownToggle}
                                    />
                                </WidgetOnOffWrapper>,
                            ]}
                            plotOptionsKeys={[chartTypes.LINE, chartTypes.AREA]}
                            plotOptionsParams={{
                                withMarkers: !showSourceTypeBreakdown,
                            }}
                        />
                    </GraphWrapper>
                ) : null}
            </WidgetContainer>
        </TrendOverTimeWrapper>
    );
};
